import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import type {
  ActiveFormDescriptorField,
  PrefillResult,
} from '@/blocks/active-form/types';
import { useActiveFormStore } from '@/blocks/active-form/useActiveFormStore';
import { useSmartCopyAmplitude } from '@/hooks/smart-copy';

import type { FormPrefillProposal } from '../useChatStore';
import { useChatStore } from '../useChatStore';
import { inferModuleFromPathname } from '../utils/inferModuleFromPathname';
import { formatProposalValue, REJECT_REASON_KEYS } from './formatProposalValue';
import styles from './ProposalCard.module.scss';

/**
 * `entityType` is a snake_case `FormId` (`action`, `third_party`,
 * `internal_audit_report`). Good enough to read inline — the alternative is a
 * FormId→label map that would need a new entry every time a register is added,
 * and silently say "undefined" when someone forgets.
 *
 * Interpolated into the translated `stale` string rather than concatenated, so
 * a locale can put the entity name wherever its grammar needs it.
 */
const humaniseEntityType = (entityType: string): string =>
  entityType.replace(/_/g, ' ');

interface ProposalCardProps {
  /** Message the proposal is attached to — the key for status transitions. */
  messageId: string;
  proposal: FormPrefillProposal;
}

/**
 * The user-facing half of suggest-and-confirm form prefill (AID-381; spec
 * `docs/ai-chat-form-prefill-spec.md` §4.4).
 *
 * Renders the values the agent proposed for the form on screen and lets the user
 * write them into it with one click. Apply is a local react-hook-form `setValue`
 * via the `useActiveFormStore` bridge — it dirties and validates the fields and
 * stops there. Nothing here persists; the user still saves the form through the
 * normal `FormContext` → tRPC → `perm_v2` path, which is what keeps a read-only
 * agent read-only (spec §7).
 */
export const ProposalCard: FC<ProposalCardProps> = ({
  messageId,
  proposal,
}) => {
  const { t } = useTranslation('common');
  const { pathname } = useLocation();
  const setProposalStatus = useChatStore((s) => s.setProposalStatus);
  // Subscribed, NOT snapshotted: the staleness guard has to re-evaluate when the
  // user navigates. The store only publishes on descriptor/record change (see
  // `ActiveFormPublisher`'s effect deps), not per keystroke, so this does not
  // re-render the panel while the user types.
  const formState = useActiveFormStore((s) => s.formState);
  const [applyResult, setApplyResult] = useState<PrefillResult | null>(null);

  /**
   * Whether the form this proposal was computed for is still the one on screen.
   *
   * DERIVED every render rather than persisted as `status: 'stale'`, because
   * staleness is reversible: navigating from Action A to B and back should make
   * the proposal applicable again, and a written status could never come back.
   * (`'stale'` remains in `FormPrefillProposalStatus` as the rendered state,
   * just not a stored one.)
   */
  const isStale =
    // An untargeted proposal is stale against every form, forever — matching it
    // on `recordId: null` would make it applicable to any create form of the
    // same entity type (see FormPrefillProposal.hasActiveForm).
    !proposal.hasActiveForm ||
    !formState ||
    formState.entityType !== proposal.entityType ||
    formState.recordId !== proposal.recordId;

  /**
   * The live descriptor entries for the form THIS proposal targets, keyed by
   * field — whichever vocabulary each field reads its values through (a closed
   * option set, or an identity one).
   *
   * Gated on `isStale` — i.e. on the record as well as the entity type — not on
   * the entity type alone. Today every published option set is entity-type
   * scoped (the org's rating taxonomy, the `common` namespace, the org's
   * custom-attribute schema), so two Actions resolve identical options and an
   * entity-type gate would give the same labels. But the descriptor is one
   * change away from carrying record-scoped options: `ControlledRating` already
   * resolves likelihood/impact bands per assessment-config VERSION
   * (`useScoringSettings`), which varies by record. Were those wired into the
   * descriptor, an entity-type gate would silently relabel record A's value with
   * record B's bands — a wrong label that reads as authoritative. Keying on the
   * record makes that unrepresentable rather than merely unlikely.
   *
   * The cost is that a stale card shows raw values instead of labels. That card
   * cannot be applied anyway, so showing the value the proposal actually carries
   * is the honest fallback.
   *
   * Identity fields (AID-394) need the record gate for a second, blunter
   * reason: their candidates resolve a NAME to a UUID, and resolving against
   * the wrong form's directory is how a card could name one colleague while
   * Apply writes another's id.
   */
  const descriptorFields = useMemo(() => {
    const byKey = new Map<string, ActiveFormDescriptorField>();
    if (isStale || !formState) {
      return byKey;
    }
    for (const field of formState.descriptor) {
      byKey.set(field.key, field);
    }

    return byKey;
  }, [formState, isStale]);

  const module = inferModuleFromPathname(pathname);
  const baseProps = useMemo(
    () => ({
      entity_type: proposal.entityType,
      module,
      field_count: proposal.fields.length,
    }),
    [proposal.entityType, module, proposal.fields.length]
  );
  // `useSmartCopyAmplitude` is reused here for its tenant/role stamping, which is
  // the part worth sharing — `logEvent` takes any event name (see its docstring).
  // The `events` map it also returns is Smart Copy's own and is not used.
  const { logEvent } = useSmartCopyAmplitude({
    entityLabel: 'AI Prefill',
    baseProps,
  });

  // One "Proposed" event per proposal, not per render. Keyed on proposalId so a
  // replacement proposal in the same turn (setProposal is last-write-wins) is
  // still counted.
  const proposedLoggedRef = useRef<string | null>(null);
  useEffect(() => {
    if (proposedLoggedRef.current === proposal.proposalId) {
      return;
    }
    proposedLoggedRef.current = proposal.proposalId;
    logEvent('AI Prefill Proposed', {});
  }, [proposal.proposalId, logEvent]);

  const handleApply = useCallback(() => {
    // Re-read at click time rather than trusting the subscribed `formState`: the
    // guard below is a render-time check and the form could have unmounted since.
    const current = useActiveFormStore.getState().formState;
    if (
      !proposal.hasActiveForm ||
      !current ||
      current.entityType !== proposal.entityType ||
      current.recordId !== proposal.recordId
    ) {
      return;
    }

    const result = current.applyPrefill(
      proposal.fields.map(({ key, value }) => ({ key, value }))
    );

    setApplyResult(result);
    setProposalStatus(messageId, 'applied');
    logEvent('AI Prefill Applied', {
      applied_count: result.applied.length,
      rejected_count: result.rejected.length,
    });
  }, [proposal, messageId, setProposalStatus, logEvent]);

  const handleDismiss = useCallback(() => {
    setProposalStatus(messageId, 'dismissed');
    logEvent('AI Prefill Dismissed', {});
  }, [messageId, setProposalStatus, logEvent]);

  if (proposal.status === 'dismissed') {
    return (
      <div className={styles.card} data-testid={'prefill-proposal-dismissed'}>
        <span className={styles.outcome}>{t('chat.prefill.dismissed')}</span>
      </div>
    );
  }

  const isApplied = proposal.status === 'applied';

  return (
    <div className={styles.card} data-testid={'prefill-proposal'}>
      <div className={styles.header}>
        <span className={styles.title}>{t('chat.prefill.title')}</span>
        <span className={styles.count}>
          {t('chat.prefill.fieldCount', { count: proposal.fields.length })}
        </span>
      </div>

      <ul className={styles.fields}>
        {proposal.fields.map((field) => {
          // The live descriptor supplies the field's own vocabulary, so a
          // closed-option value renders as the label the user sees on the form
          // (AID-393) and a proposed name renders as the directory's own
          // spelling of that person (AID-394). Read from `formState` rather than
          // carried on the proposal: both are resolved per-org from the same
          // sources the form's controls read, and the wire deliberately carries
          // neither on the way DOWN. Empty whenever the proposal is stale (see
          // `descriptorFields`), so the raw value is shown instead of a label
          // borrowed from another record.
          const value = formatProposalValue(
            field.value,
            descriptorFields.get(field.key)
          );

          return (
            <li key={field.key} className={styles.field}>
              <div className={styles.fieldLabel}>{field.label}</div>
              <div className={styles.fieldValue}>
                {value ?? t('chat.prefill.emptyValue')}
              </div>
              {field.reason && (
                <div className={styles.fieldReason}>{field.reason}</div>
              )}
            </li>
          );
        })}
      </ul>

      {isApplied ? (
        <>
          <div className={styles.outcome} data-testid={'prefill-applied'}>
            {applyResult && applyResult.applied.length === 0
              ? t('chat.prefill.noneApplied')
              : t('chat.prefill.applied')}
          </div>
          {applyResult && applyResult.rejected.length > 0 && (
            <div className={styles.rejected} data-testid={'prefill-rejected'}>
              {applyResult.rejected
                .map(
                  ({ key, reason }) =>
                    `${key} — ${t(REJECT_REASON_KEYS[reason])}`
                )
                .join('; ')}
            </div>
          )}
        </>
      ) : (
        <>
          {isStale && (
            <div className={styles.stale} data-testid={'prefill-stale'}>
              {t('chat.prefill.stale', {
                entityName: humaniseEntityType(proposal.entityType),
              })}
            </div>
          )}
          <div className={styles.actions}>
            <button
              type={'button'}
              className={`${styles.button} ${styles.applyButton}`}
              onClick={handleApply}
              disabled={isStale}
            >
              {t('chat.prefill.apply')}
            </button>
            <button
              type={'button'}
              className={styles.button}
              onClick={handleDismiss}
            >
              {t('chat.prefill.dismiss')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
